import { getIsLargeUI } from 'app/utils/web-helpers';
import type { ScreenParameters, SpecificNavigationProp } from 'app/types/navigation';
import type { FreeSubSignalData } from 'app/network/signals';
import type { IntroModalUse } from 'app/screens/intro/intro-modal';
import { SharerType } from 'app/types/preferences';
import type { User } from 'app/types/user';
import {
  canUseListingTags,
  DynamicFeatureFlag,
  FeatureFlag,
  isFeatureEnabled,
  isNotWhiteLabel,
} from 'app/utils/helpers';

export function canUseCentralizedCreation(
  featureFlags: User['featureFlags'] | undefined,
  isUserPending: boolean,
): boolean {
  return (
    !isUserPending &&
    Boolean(featureFlags?.enabled?.includes(DynamicFeatureFlag.CentralizedCreation)) &&
    isFeatureEnabled(FeatureFlag.publishListings, featureFlags)
  );
}

export function canUseOfferHub(featureFlags: User['featureFlags'] | undefined, isUserPending: boolean): boolean {
  return !isUserPending && isNotWhiteLabel() && isFeatureEnabled(DynamicFeatureFlag.OfferHub, featureFlags);
}

/** Main-app publishing shortcuts use the swipe hub only for eligible users and app flavors. */
export function navigateToQuickPublishFlow<T extends keyof ScreenParameters>(
  navigation: SpecificNavigationProp<T>,
  featureFlags?: User['featureFlags'],
  isUserPending = true,
  params?: ScreenParameters['offer-hub'],
) {
  if (canUseCentralizedCreation(featureFlags, isUserPending)) {
    if (getIsLargeUI()) {
      navigation.navigate('offer-hub', params);
    } else {
      navigation.navigate('main', { screen: 'publish', params });
    }
    return;
  }
  if (canUseOfferHub(featureFlags, isUserPending)) {
    navigation.navigate('offer-hub', params);
    return;
  }
  navigateToLegacyQuickPublishFlow(navigation, { featureFlags });
}

/** Finish onboarding in the hub when the user has access, otherwise preserve the legacy flow. */
export function navigateToPostSignupFlow(
  navigation: SpecificNavigationProp<'presentCommunity'>,
  featureFlags?: User['featureFlags'],
  isUserPending = true,
) {
  if (canUseOfferHub(featureFlags, isUserPending)) {
    navigation.reset({
      routes: [{ name: 'main' }, { name: 'offer-hub', params: { newListingSource: 'onboardingV2' } }],
    });
    return;
  }

  navigation.navigate('intro', { screen: 'lenderNext' }, { pop: true });
}

/** Guided tutorials and legacy fallbacks respect each brand's catalog. */
export function navigateToLegacyQuickPublishFlow<T extends keyof ScreenParameters>(
  navigation: SpecificNavigationProp<T>,
  options?: { fromBabillard?: boolean; featureFlags?: User['featureFlags'] },
) {
  if (canUseListingTags(options?.featureFlags)) {
    navigation.navigate(
      'intro',
      {
        screen: 'interests',
        params: {
          sharerType: SharerType.lender,
          skipExplain: true,
          fromBabillard: options?.fromBabillard ?? true,
        },
      },
      { pop: true },
    );
  } else {
    navigation.navigate('quickNew', undefined, { pop: true });
  }
}

export function navigateToIntro<T extends keyof ScreenParameters>(
  navigation: SpecificNavigationProp<T>,
  freeSubAvailable: boolean | FreeSubSignalData['program'],
  use: IntroModalUse,
  customTemplate?: string,
) {
  navigation.navigate(
    'intro',
    {
      screen: 'introModal',
      params: { freeSubAvailable, use, customTemplate },
    },
    { pop: true },
  );
}
